import React from 'react';
import classes from './RolesSettings.module.scss';
import { Button, Input } from 'antd';
import Assets from 'assets/icons';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import RowLoader from 'components/Sceletons/RowLoader';
import fetchGroupsAndUsers, { defaultGroups } from 'store/reducers/usersGroupCreator';
import { setConfigurableRole, setIsConfigureRolesOpen } from 'store/slices/dataUsersSlice';
import { IDataUserTable } from 'interfaces';
import cn from 'classnames';

const RolesSettings = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  
  const { dataUserTable, isGroupsAndUsersLoading } = useAppSelector((state) => state.dataUsers);
  const { userPermissions } = useAppSelector((state) => state.userData);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const correctHeight = userPermissions.can_create_group_all_roles
    ? classes.withFooter
    : classes.withoutFooter;

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  React.useEffect(() => {
    if (dataRoom && !dataUserTable.length && !isGroupsAndUsersLoading) {
      dispatch(fetchGroupsAndUsers({ room_id: dataRoom?.id }));
    }
  }, [dataRoom?.id]);

  const filteredDataUserTable = React.useMemo(
    () =>
      dataUserTable.filter(({ name }) => defaultGroups.includes(name!)
        ? t(`Users.table.${name}`)?.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
        : name?.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
      ),
    [dataUserTable, searchTerm]
  );

  const chooseRole = (group_id: string) => {
    const targetGroup = dataUserTable.find(group => group.id === group_id);    
    dispatch(setConfigurableRole(targetGroup as IDataUserTable));
    dispatch(setIsConfigureRolesOpen(true));
  };
  
  const createRole = () => {    
    dispatch(setConfigurableRole({
      id: 'new',
      name: '',
      role: {
        id: 'new',
        name: '',
        permissions: []
      }
    }));
    dispatch(setIsConfigureRolesOpen(true));
  };

const showLoader = isGroupsAndUsersLoading || !dataUserTable.length

  return (
    <div className={classes.rSidebarContent}>

      <div className={classes.search}>
        <Input
          suffix={<Assets.SearchIcon className={classes.searchIcon} />}
          allowClear
          value={searchTerm}
          onChange={onSearch}
          placeholder={t('Users.modals.configureRoles.searchRole')}
          style={{ width: '100%' }}
        />
      </div>

      <div className={cn(classes.roles, correctHeight)}>
        {showLoader
          ?  [1, 2, 3].map((el, i) => (<div key={`loader-${i}`} className={classes.buttonItem}>
            <RowLoader width={200} height={30} padding={'0px'} />
          </div>))
          : ( !filteredDataUserTable[0]
            ? <div className={classes.empty}>{t(`rightSideBar.rolesSection.notFound`)}</div>
            : filteredDataUserTable.map(group => (
              <div key={group.id} className={classes.buttonItem} onClick={() => chooseRole(group.id)}>
                <div className={classes.buttonText}>
                  <div className={classes.group}>{defaultGroups.includes(group.name!) ? t(`Users.table.${group.name}`) : group.name}</div>
                  <div className={classes.members}>{group.children?.length || '0'} {t(`rightSideBar.rolesSection.users`)}</div>
                </div>
                <div className={classes.buttonPostfix}>
                  <Assets.RightIcon />
                </div>
              </div>
            ))
          )
        }
      </div>

      {userPermissions.can_create_group_all_roles && <div className={classes.footer}>
        <Button className={classes.createButton} onClick={createRole} icon={<Assets.RoleIcon />} type='primary'>
          {t(`rightSideBar.rolesSection.create`)}
        </Button>
      </div>}
    </div>
  );
}

export default RolesSettings;
